:root {
  --primary-color: #635BFF;
  --primary-hover: #574ce0;
  --primary-light: #EBF4FF;
  --success-color: #38A169;
  --warning-color: #F59E0B;
  --danger-color: #E53E3E;
  --background-light: #F7F9FC;
  --card-background: white;
  --text-primary: #2D3748;
  --text-secondary: #718096;
  --border-color: #E2E8F0;
  --shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}
/* Add these at the top of your file */

/* Auth container classes */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner {
  background-color: var(--primary-color);
  color: white;
  padding: 30px 0;
  text-align: center;
  margin-bottom: 30px;
  border-radius: 0 0 8px 8px;
}

.content {
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: var(--shadow);
  margin-bottom: 30px;
}

.setupContainer {
  text-align: center;
  padding: 20px 0;
}

.dashboardActions {
  text-align: center;
  padding: 20px 0;
}

.actionButtons {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.errorMessage {
  color: #E53E3E;
  background-color: #FED7D7;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: left;
  border-left: 4px solid #E53E3E;
}

.loadingContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  padding: 0 20px;
}

/* Marketing Container for non-authenticated users */
.marketingContainer {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  color: var(--text-primary);
  line-height: 1.6;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #635BFF 0%, #44337A 100%);
  color: white;
  padding: 120px 24px 100px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo {
  margin-bottom: 40px;
}

.hero h1 {
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 24px;
  line-height: 1.2;
}

.hero p {
  font-size: 22px;
  max-width: 760px;
  margin: 0 auto 40px;
  opacity: 0.9;
}

.ctaButtons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.primaryButton {
  background-color: white;
  color: #635BFF;
  font-weight: 600;
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  font-size: 18px;
}

.primaryButton:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.secondaryButton {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  font-weight: 500;
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 18px;
}

.secondaryButton:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

/* Features Section */
.features {
  padding: 100px 24px;
  background-color: white;
  text-align: center;
}

.sectionTitle {
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.sectionSubtitle {
  font-size: 20px;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto 60px;
}

.featureGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.feature {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.featureIcon {
  background-color: var(--primary-light);
  color: var(--primary-color);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 32px;
}

.feature h3 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.feature p {
  color: var(--text-secondary);
  font-size: 16px;
}

/* How It Works Section */
.howItWorks {
  padding: 100px 24px;
  background-color: var(--background-light);
}

.steps {
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 40px;
}

.step {
  background-color: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stepNumber {
  background-color: var(--primary-color);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
}

.step h3 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.step p {
  color: var(--text-secondary);
}

.stepContent {
  text-align: center;
  width: 100%;
}

/* Pricing Section */
.pricing {
  padding: 100px 24px;
  background-color: white;
  text-align: center;
}

.pricingContainer {
  max-width: 1000px;
  margin: 0 auto;
}

.pricingIntro {
  margin-bottom: 40px;
}

.pricingCard {
  background-color: white;
  border-radius: 12px;
  padding: 48px;
  box-shadow: 0 0 0 2px var(--primary-color), 0 8px 20px rgba(99, 91, 255, 0.1);
  text-align: left;
  max-width: 600px;
  margin: 0 auto;
}

.pricingHeader {
  margin-bottom: 32px;
}

.pricingHeader h3 {
  font-size: 28px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 8px;
}

.pricingHeader p {
  font-size: 18px;
  color: var(--text-secondary);
}

.price {
  font-size: 40px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 24px;
}

.priceDetail {
  font-size: 18px;
  color: var(--text-secondary);
  margin-top: -20px;
  margin-bottom: 24px;
}

.pricingFeatures {
  list-style-type: none;
  padding: 0;
  margin: 0 0 32px 0;
}

.pricingFeatures li {
  margin-bottom: 12px;
  padding-left: 28px;
  position: relative;
}

.pricingFeatures li:before {
  content: "✓";
  color: var(--success-color);
  position: absolute;
  left: 0;
  font-weight: bold;
}

/* Testimonials Section */
.testimonials {
  padding: 100px 24px;
  background-color: var(--background-light);
  text-align: center;
}

.testimonialGrid {
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
}

.testimonialCard {
  background-color: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: var(--shadow);
  text-align: left;
}

.testimonialText {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 20px;
  font-style: italic;
}

.testimonialAuthor {
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-primary);
}

.testimonialRole {
  font-size: 14px;
  color: var(--text-secondary);
}

/* ROI Calculator Section */
.roiCalculator {
  padding: 100px 24px;
  background-color: white;
  text-align: center;
}

.calculatorContainer {
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
}

.roiTable {
  width: 100%;
  border-collapse: collapse;
  margin: 30px 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow);
}

.roiTable th, .roiTable td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.roiTable th {
  background-color: var(--background-light);
  font-weight: 600;
}

.roiHighlight {
  background-color: var(--primary-light);
  font-weight: 600;
}

/* Final CTA */
.finalCta {
  background: linear-gradient(135deg, #635BFF 0%, #44337A 100%);
  color: white;
  padding: 80px 24px;
  text-align: center;
}

.finalCta h2 {
  margin-bottom: 32px;
  font-size: 36px;
  font-weight: 700;
}

.authButtons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Dashboard Container for authenticated users */
.dashboardContainer {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-light);
}

.welcomeSection {
  padding: 48px 24px;
  background-color: white;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 24px;
}

.welcomeHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.welcomeHeader h1 {
  font-size: 28px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.dashboardContent {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 48px;
  width: 100%;
}

.noRestaurantsMessage {
  text-align: center;
  padding: 100px 0;
}

.noRestaurantsMessage h2 {
  font-size: 24px;
  color: var(--text-primary);
  margin-bottom: 16px;
}

.noRestaurantsMessage p {
  font-size: 18px;
  color: var(--text-secondary);
  margin-bottom: 32px;
}

/* Feature Card for marketing page */
.featureCard {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow);
  text-align: center;
  transition: transform 0.2s ease;
}

.featureCard:hover {
  transform: translateY(-5px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 36px;
  }
  
  .hero p {
    font-size: 18px;
  }
  
  .sectionTitle {
    font-size: 32px;
  }
  
  .pricingCard {
    padding: 32px;
  }
  
  .featureGrid, .steps {
    grid-template-columns: 1fr;
  }
}

/* Animation for loading states */
@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.loading {
  animation: pulse 1.5s infinite;
}
/* Add this class to your Home.module.css file */
.errorMessage {
  color: #E53E3E; /* Red color for error */
  background-color: #FED7D7; /* Light red background */
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: left;
  border-left: 4px solid #E53E3E;
}